import { useParams, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { ArrowLeft, Calendar, Clock, MapPin, Users, AlertTriangle, Award } from "lucide-react";
import DanceLabApplicationForm from "@/components/forms/DanceLabApplicationForm";
import { useDanceLabEvent } from "@/hooks/useDanceLabEvents";
import { Skeleton } from "@/components/ui/skeleton";
import losAngelesImg from "@/assets/losangeles.webp";
import newYorkImg from "@/assets/newyork.webp";

const eventImages: Record<string, string> = {
  "los-angeles": losAngelesImg,
  "new-york": newYorkImg,
};

const DanceLabEvent = () => {
  const { citySlug } = useParams<{ citySlug: string }>();
  const navigate = useNavigate();
  const { data: event, isLoading } = useDanceLabEvent(citySlug);

  useEffect(() => {
    if (!isLoading && !event) navigate("/dance-lab", { replace: true });
  }, [event, isLoading, navigate]);

  if (isLoading) {
    return (
      <main className="relative min-h-screen bg-black flex items-center justify-center">
        <div className="space-y-6 w-full max-w-2xl px-8">
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-40 w-full rounded-2xl" />
        </div>
      </main>
    );
  }

  if (!event) return null;

  const heroImage = eventImages[event.slug];
  const siteUrl = "https://klauyros.com";
  const pageUrl = `${siteUrl}/dance-lab/${event.slug}`;
  const defaultOgImage = "https://cbuxrdarzwtolyjhnhzw.supabase.co/storage/v1/object/public/og-images/brand-logo.png";
  const ogImage = event.ogImage || defaultOgImage;
  const ogTitle = `Dance Lab — ${event.city} | Klau y Ros`;
  const ogDescription = event.description?.substring(0, 155) + "…";

  return (
    <main className="relative min-h-screen">
      <Helmet>
        <title>{ogTitle}</title>
        <meta name="description" content={ogDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={ogTitle} />
        <meta property="og:description" content={ogDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={pageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={ogTitle} />
        <meta name="twitter:description" content={ogDescription} />
        <meta name="twitter:image" content={ogImage} />
        <link rel="canonical" href={pageUrl} />
      </Helmet>
      {/* Fixed full-page background image */}
      {heroImage && (
        <div className="fixed inset-0 z-0">
          <img
            src={heroImage}
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/70 to-black/90" />
        </div>
      )}
      {!heroImage && <div className="fixed inset-0 z-0 bg-black" />}

      {/* HERO */}
      <section
        className="relative z-10 min-h-[60vh] flex items-center overflow-hidden"
        style={{ paddingTop: "88px" }}
      >

        <div className="relative z-10 max-w-6xl mx-auto px-8 md:px-12 lg:px-16 w-full">
          {/* Back link */}
          <motion.button
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            onClick={() => navigate("/dance-lab")}
            className="flex items-center gap-2 mb-8 text-white/60 hover:text-white transition-colors"
            style={{ fontFamily: "'Grift', sans-serif" }}
          >
            <ArrowLeft size={18} />
            <span>Back to all labs</span>
          </motion.button>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <span
              className="inline-block px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-6"
              style={{
                background: "linear-gradient(135deg, #D4AF37 0%, #F4E4C1 50%, #C9A961 100%)",
                fontFamily: "'Grift', sans-serif",
                color: "#1a1a1a",
              }}
            >
              {event.spotsAvailable} spots available
            </span>

            <h1
              className="mb-4 leading-tight"
              style={{
                fontFamily: "'Grift', sans-serif",
                fontSize: "clamp(36px, 7vw, 80px)",
                fontWeight: 700,
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
                color: "#FFFFFF",
                textShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
                fontStyle: "italic",
              }}
            >
              <span style={{ transform: "skewX(-5deg)", display: "inline-block" }}>
                Dance Lab -{" "}
                <span style={{ color: "#E67E22" }}>{event.city}</span>
              </span>
            </h1>

            {/* Subtitle & Level */}
            {event.subtitle && (
              <p
                className="text-lg md:text-xl mb-1"
                style={{ fontFamily: "'Grift', sans-serif", color: "rgba(255, 255, 255, 0.8)", fontStyle: "italic" }}
              >
                {event.subtitle}
              </p>
            )}
            {event.level && (
              <p
                className="text-sm md:text-base mb-4"
                style={{ fontFamily: "'Playfair Display', serif", color: "rgba(255, 255, 255, 0.6)", fontStyle: "italic" }}
              >
                {event.level}
              </p>
            )}

            {/* Certification Badge */}
            <div
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold mb-2"
              style={{
                background: "linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(244, 228, 193, 0.08) 100%)",
                border: "1px solid rgba(212, 175, 55, 0.4)",
                fontFamily: "'Grift', sans-serif",
                color: "#D4AF37",
                fontStyle: "italic",
              }}
            >
              <Award size={16} style={{ color: "#D4AF37" }} />
              <span>Official Klau & Ros Certification Included</span>
            </div>

            <div className="flex flex-wrap gap-6 mt-6">
              <div className="flex items-center gap-2">
                <Calendar size={18} style={{ color: "#E67E22" }} />
                <span style={{ fontFamily: "'Playfair Display', serif", color: "rgba(255,255,255,0.9)", fontStyle: "italic" }}>
                  {event.dates}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={18} style={{ color: "#E67E22" }} />
                <span style={{ fontFamily: "'Playfair Display', serif", color: "rgba(255,255,255,0.9)", fontStyle: "italic" }}>
                  {event.venue || `${event.city}, ${event.country}`}
                </span>
              </div>
              {event.totalHours && (
                <div className="flex items-center gap-2">
                  <Clock size={18} style={{ color: "#E67E22" }} />
                  <span style={{ fontFamily: "'Playfair Display', serif", color: "rgba(255,255,255,0.9)", fontStyle: "italic" }}>
                    {event.totalHours}
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* DESCRIPTION */}
      <section className="relative z-10 py-16 md:py-24 px-8 md:px-12 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-lg md:text-xl leading-relaxed"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: "rgba(255, 255, 255, 0.9)",
              fontStyle: "italic",
            }}
          >
            {event.description}
          </motion.p>

          {/* Certification callout */}
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-base md:text-lg"
            style={{
              fontFamily: "'Playfair Display', serif",
              color: "rgba(255, 255, 255, 1)",
              fontStyle: "italic",
            }}
          >
            Upon completion, receive an official Klau & Ros certification recognizing your training.
          </motion.p>

          {/* Selection Note Callout */}
          {event.selectionNote && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-8 rounded-2xl p-6 md:p-8 flex gap-4 items-start"
              style={{
                background: "linear-gradient(135deg, rgba(230, 126, 34, 0.15) 0%, rgba(255, 158, 79, 0.08) 100%)",
                border: "1px solid rgba(230, 126, 34, 0.3)",
                backdropFilter: "blur(10px)",
              }}
            >
              <AlertTriangle size={24} style={{ color: "#E67E22", flexShrink: 0, marginTop: "2px" }} />
              <p
                className="text-sm md:text-base"
                style={{
                  fontFamily: "'Playfair Display', serif",
                  color: "rgba(255, 255, 255, 0.9)",
                  fontStyle: "italic",
                  lineHeight: 1.6,
                }}
              >
                {event.selectionNote}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* SCHEDULE */}
      <section className="relative z-10 py-16 md:py-24 px-8 md:px-12 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12 leading-tight"
            style={{
              fontFamily: "'Grift', sans-serif",
              fontSize: "clamp(24px, 4vw, 40px)",
              fontWeight: 700,
              color: "#FFFFFF",
              fontStyle: "italic",
              transform: "skewX(-5deg)",
            }}
          >
            <span className="inline-block relative" style={{ color: "#E67E22" }}>
              Schedule
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                style={{ backgroundColor: "#E67E22" }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </motion.h2>

          <div className="space-y-6">
            {event.schedule.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex gap-6 md:gap-8 items-start p-6 md:p-8 rounded-2xl transition-all duration-300"
                style={{
                  background: "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)",
                  border: "1px solid rgba(255, 255, 255, 0.15)",
                  backdropFilter: "blur(10px)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "rgba(230, 126, 34, 0.3)";
                  e.currentTarget.style.background = "linear-gradient(135deg, rgba(230, 126, 34, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "rgba(255, 255, 255, 0.15)";
                  e.currentTarget.style.background = "linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)";
                }}
              >
                <div className="flex-shrink-0">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center"
                    style={{
                      background: "linear-gradient(135deg, #E67E22 0%, #FF9E4F 100%)",
                      boxShadow: "0 0 15px rgba(230, 126, 34, 0.5)",
                    }}
                  >
                    <Clock size={20} color="white" />
                  </div>
                </div>
                <div>
                  <h3
                    className="text-lg md:text-xl font-bold mb-1"
                    style={{ fontFamily: "'Grift', sans-serif", color: "#E67E22", fontStyle: "italic", transform: "skewX(-5deg)" }}
                  >
                    {item.day}
                  </h3>
                  <p className="text-sm mb-2" style={{ fontFamily: "'Grift', sans-serif", color: "rgba(255,255,255,0.6)" }}>
                    {item.time}
                  </p>
                  {item.description && (
                    <p style={{ fontFamily: "'Playfair Display', serif", color: "rgba(255,255,255,0.9)", fontStyle: "italic" }}>
                      {item.description}
                    </p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Schedule footer text */}
          {event.scheduleFooter && (
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-10 text-center text-base md:text-lg uppercase tracking-wider"
              style={{
                fontFamily: "'Grift', sans-serif",
                color: "rgba(255, 255, 255, 0.8)",
                fontStyle: "italic",
                letterSpacing: "0.05em",
              }}
            >
              {event.scheduleFooter}
            </motion.p>
          )}
        </div>
      </section>

      {/* PRICING */}
      <section className="relative z-10 py-16 md:py-24 px-8 md:px-12 lg:px-16">
        <div className="max-w-5xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-12 leading-tight"
            style={{
              fontFamily: "'Grift', sans-serif",
              fontSize: "clamp(24px, 4vw, 40px)",
              fontWeight: 700,
              color: "#FFFFFF",
              fontStyle: "italic",
              transform: "skewX(-5deg)",
            }}
          >
            <span className="inline-block relative" style={{ color: "#E67E22" }}>
              Pricing
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                style={{ backgroundColor: "#E67E22" }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </motion.h2>

          <div className="grid md:grid-cols-2 gap-6 md:gap-8">
            {event.pricing.map((tier, index) => (
              <motion.div
                key={tier.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="rounded-3xl p-8 md:p-10 transition-all duration-300 text-center"
                style={{
                  background: tier.highlighted
                    ? "linear-gradient(135deg, rgba(230, 126, 34, 0.2) 0%, rgba(255, 158, 79, 0.1) 100%)"
                    : "linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
                  backdropFilter: "blur(20px)",
                  border: tier.highlighted
                    ? "2px solid rgba(230, 126, 34, 0.5)"
                    : "1px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: tier.highlighted
                    ? "0 8px 60px rgba(230, 126, 34, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.15)"
                    : "0 8px 40px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.15)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                }}
              >
                {tier.highlighted && (
                  <span
                    className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4"
                    style={{
                      background: "linear-gradient(90deg, #E67E22, #FF9E4F)",
                      color: "#FFFFFF",
                      fontFamily: "'Grift', sans-serif",
                    }}
                  >
                    Most Popular
                  </span>
                )}
                <h3
                  className="text-xl font-bold mb-2"
                  style={{ fontFamily: "'Grift', sans-serif", color: "#FFFFFF", fontStyle: "italic" }}
                >
                  {tier.label}
                </h3>
                <p
                  className="text-4xl md:text-5xl font-bold mb-4"
                  style={{
                    fontFamily: "'Grift', sans-serif",
                    color: "#E67E22",
                    fontStyle: "italic",
                    transform: "skewX(-5deg)",
                  }}
                >
                  ${tier.price}
                </p>
                <p
                  className="text-sm"
                  style={{ fontFamily: "'Playfair Display', serif", color: "rgba(255,255,255,0.7)", fontStyle: "italic" }}
                >
                  {tier.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="text-center mt-8 text-sm"
            style={{ fontFamily: "'Playfair Display', serif", color: "rgba(255,255,255,0.5)", fontStyle: "italic" }}
          >
            {event.pricingNote}
          </motion.p>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section className="relative z-10 py-16 md:py-24 px-8 md:px-12 lg:px-16">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-4 leading-tight text-center"
            style={{
              fontFamily: "'Grift', sans-serif",
              fontSize: "clamp(24px, 4vw, 40px)",
              fontWeight: 700,
              color: "#FFFFFF",
              fontStyle: "italic",
              transform: "skewX(-5deg)",
            }}
          >
            <span className="inline-block relative" style={{ color: "#E67E22" }}>
              Apply Now
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 rounded-full"
                style={{ backgroundColor: "#E67E22" }}
                initial={{ scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3 }}
              />
            </span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-12"
            style={{ fontFamily: "'Playfair Display', serif", color: "rgba(255,255,255,0.7)", fontStyle: "italic", fontSize: "clamp(14px, 1.5vw, 18px)" }}
          >
            Application deadline: {event.applicationDeadline}
          </motion.p>

          <DanceLabApplicationForm
            eventSlug={event.slug}
            eventTitle={`Endless Dance Lab - ${event.city}`}
          />
        </div>
      </section>
    </main>
  );
};

export default DanceLabEvent;
